import React, { useEffect, useMemo } from 'react';
import LinearProgress from '@material-ui/core/LinearProgress';
import { StatusManager } from 'common/components/StatusManager';
import { NewCandidateForm } from 'features/new-candidate/components';
import { userHasRole, userHasRoles } from 'services/Authorization';
import strings from 'strings';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { Roles } from 'UI/constants/roles';
import { UIStatus } from 'UI/constants/status';

import { useFetchData } from './NewCandidateDataLoader.hooks';
import { formatResults } from './NewCandidateDataLoader.utils';

export const NewCandidateDataLoader = ({
  loaderParams,
  fromSource,
  onLoad,
  onRegisterLeaveGuard,
  preselectedCompany
}) => {
  const { id, isFromDraft, contactId, hiringId } = loaderParams;
  const { status, results, error } = useFetchData({
    fromSource,
    loaderParams
  });

  const isSuccess = status === UIStatus.Success;
  const isUserCoach = userHasRole(Roles.Coach);
  const hasSuperiorRole = userHasRoles([Roles.Operations, Roles.DataCoordinator]);

  const formattedResults = useMemo(() => {
    if (!isSuccess) return null;

    return formatResults({
      contactId,
      fromSource,
      hiringId,
      id,
      isFromDraft,
      results
    });
  }, [contactId, fromSource, hiringId, id, isFromDraft, isSuccess, results]);

  useEffect(() => {
    if (isSuccess && isFromDraft) {
      onLoad(results?.draft?.fromSource ?? '');
    }
  }, [isFromDraft, isSuccess, onLoad, results]);

  return (
    <StatusManager
      status={status}
      slots={{
        Error: <EmptyPlaceholder title={strings.shared.ui.unknownError.title} subtitle={error} />,
        Loading: <LinearProgress />,
        Default: (
          <NewCandidateForm
            hasSuperiorRole={hasSuperiorRole}
            isUserCoach={isUserCoach}
            onRegisterLeaveGuard={onRegisterLeaveGuard}
            preselectedCompany={preselectedCompany}
          />
        )
      }}
    >
      <NewCandidateForm
        formattedResults={formattedResults}
        hasSuperiorRole={hasSuperiorRole}
        isUserCoach={isUserCoach}
        onRegisterLeaveGuard={onRegisterLeaveGuard}
        preselectedCompany={preselectedCompany}
      />
    </StatusManager>
  );
};
